import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input, Select } from '../../components/ui/FormField.js';
import { type Coordinates, LocationMap } from '../../components/ui/LocationMap.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { Switch } from '../../components/ui/Switch.js';
import { type Zone, ZoneEditor } from '../../components/ui/ZoneEditor.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useBackNavigation } from '../../hooks/useBackNavigation.js';
import { useSaveMutation } from '../../hooks/useSaveMutation.js';
import { api } from '../../services/api.js';
import { getCities, getCountries, getStates } from '../../services/geo.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

interface GeoRefs {
  country_id?: string;
  country_name?: string;
  state_id?: string;
  state_name?: string;
  city_id?: string;
  city_name?: string;
}

interface LocationFormData extends GeoRefs {
  name: string;
  address_line_1: string;
  postal_code: string;
  latitude?: number;
  longitude?: number;
  operating_hours?: { start: string; end: string } | null;
  /** 335: whether customers can see and use this location. */
  is_active?: boolean;
  /** 416 (#1431): the drawn geo-fence ([lng, lat] corners). */
  zone?: Zone | null;
}

export function LocationFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useBackNavigation('/locations');
  const isEdit = Boolean(id);
  // 041 US3: users who cannot edit a location get a read-only map (no drag/search).
  const ability = useAbility();
  const canEdit = ability.can('update', 'Location') || ability.can('manage', 'Location');

  const { data: location } = useQuery({
    queryKey: RESOURCE_KEYS.location.one(id as string),
    queryFn: () => api.get<LocationFormData>(`/locations/${id}`),
    enabled: isEdit,
  });

  // 041 US1: the map position is controlled state (the pin/search drive it).
  const [coords, setCoords] = useState<Coordinates | null>(null);
  // 335 (FR-001): the Active switch, saved with the form. `null` until the operator touches it, so it follows the
  // loaded location; the platform refuses switching off a location in use and the form shows why.
  const [activeChoice, setActiveChoice] = useState<boolean | null>(null);
  const active = activeChoice ?? location?.is_active !== false;
  useEffect(() => {
    if (location?.latitude != null && location?.longitude != null) {
      setCoords({ latitude: location.latitude, longitude: location.longitude });
    }
  }, [location]);

  // 041 US2: cascading country → state → city selections (controlled).
  const [geo, setGeo] = useState<GeoRefs>({});
  useEffect(() => {
    if (location) {
      setGeo({
        country_id: location.country_id,
        country_name: location.country_name,
        state_id: location.state_id,
        state_name: location.state_name,
        city_id: location.city_id,
        city_name: location.city_name,
      });
    }
  }, [location]);

  const countriesQuery = useQuery({ queryKey: ['geo-countries'], queryFn: getCountries });
  const statesQuery = useQuery({
    queryKey: ['geo-states', geo.country_id],
    queryFn: () => getStates(geo.country_id as string),
    enabled: Boolean(geo.country_id),
  });
  const citiesQuery = useQuery({
    queryKey: ['geo-cities', geo.state_id],
    queryFn: () => getCities(geo.state_id as string),
    enabled: Boolean(geo.state_id),
  });

  function onCountryChange(id: string) {
    const name = countriesQuery.data?.find((c) => c.id === id)?.name;
    // Changing the country resets the now-invalid state + city (INV-4).
    setGeo({ country_id: id || undefined, country_name: name });
  }
  function onStateChange(id: string) {
    const name = statesQuery.data?.find((s) => s.id === id)?.name;
    setGeo((g) => ({
      ...g,
      state_id: id || undefined,
      state_name: name,
      city_id: undefined,
      city_name: undefined,
    }));
  }
  function onCityChange(id: string) {
    const name = citiesQuery.data?.find((c) => c.id === id)?.name;
    setGeo((g) => ({ ...g, city_id: id || undefined, city_name: name }));
  }

  // 161: saves go through one hook that invalidates BOTH the location list and this location's own
  // cache entry. Before it, only the list was invalidated, so reopening this form inside the
  // 30-second stale window served the value from before the save — and saving that form wrote the
  // old value back, silently reverting the operator's edit.
  const mutation = useSaveMutation<Partial<LocationFormData>>({
    resource: 'location',
    id,
    mutationFn: (data) =>
      isEdit ? api.patch(`/locations/${id}`, data) : api.post('/locations', data),
    onSaved: () => navigate('/locations'),
  });

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const {
      operating_hours_start: start,
      operating_hours_end: end,
      ...rest
    } = Object.fromEntries(fd.entries());
    const data = rest as unknown as Partial<LocationFormData>;
    // Both times set → nested window; cleared on edit → null unsets it (039 FR-011).
    if (start && end) data.operating_hours = { start: String(start), end: String(end) };
    else if (isEdit) data.operating_hours = null;
    // 041 US1: merge the controlled map position (flat latitude/longitude).
    if (coords) {
      data.latitude = coords.latitude;
      data.longitude = coords.longitude;
    }
    // 041 US2: merge the controlled country/state/city ids + denormalized names.
    Object.assign(data, geo);
    if (isEdit && canEdit) data.is_active = active;
    void mutation.save(data);
  }

  return (
    <div>
      <PageHeader
        title={isEdit ? 'Edit Location' : 'Add Location'}
        description={isEdit ? 'Update location details' : 'Create a new pickup/dropoff location'}
        fallback="/locations"
      />
      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Location details</h2>
          <div className="space-y-4">
            <FormField label="Location Name" name="name" required>
              <Input name="name" defaultValue={location?.name ?? ''} />
            </FormField>
            {isEdit && canEdit && (
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-ink">Active</p>
                  <p className="text-xs text-steel">
                    Customers see an active location and can choose it. A location with cars,
                    upcoming bookings or campus customers cannot be switched off.
                  </p>
                </div>
                <Switch
                  checked={active}
                  onChange={() => setActiveChoice(!active)}
                  ariaLabel="Active"
                />
              </div>
            )}
            <FormField label="Address" name="address_line_1">
              <Input name="address_line_1" defaultValue={location?.address_line_1 ?? ''} />
            </FormField>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Address</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Country" name="country_id">
              <Select
                id="country_id"
                value={geo.country_id ?? ''}
                onChange={(e) => onCountryChange(e.target.value)}
              >
                <option value="">Select country…</option>
                {countriesQuery.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="State" name="state_id">
              <Select
                id="state_id"
                value={geo.state_id ?? ''}
                onChange={(e) => onStateChange(e.target.value)}
                disabled={!geo.country_id}
              >
                <option value="">Select state…</option>
                {statesQuery.data?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="City" name="city_id">
              <Select
                id="city_id"
                value={geo.city_id ?? ''}
                onChange={(e) => onCityChange(e.target.value)}
                disabled={!geo.state_id}
              >
                <option value="">Select city…</option>
                {citiesQuery.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="Postal Code" name="postal_code">
              <Input name="postal_code" defaultValue={location?.postal_code ?? ''} />
            </FormField>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Map position</h2>
          <p className="mb-3 text-xs text-muted">
            Search an address or drag the pin to set the exact pickup/dropoff location.
          </p>
          <LocationMap value={coords} onChange={setCoords} interactive={canEdit} />
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Operating hours (Malaysia time)</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Opens At" name="operating_hours_start">
              <Input
                type="time"
                id="operating_hours_start"
                name="operating_hours_start"
                defaultValue={location?.operating_hours?.start ?? ''}
              />
            </FormField>
            <FormField label="Closes At" name="operating_hours_end">
              <Input
                type="time"
                id="operating_hours_end"
                name="operating_hours_end"
                defaultValue={location?.operating_hours?.end ?? ''}
              />
            </FormField>
          </div>
        </Card>

        {mutation.error instanceof Error && (
          <p className="text-sm text-red-600">{mutation.error.message}</p>
        )}

        <div className="flex gap-3">
          <Button type="submit" loading={mutation.isPending}>
            {isEdit ? 'Save Changes' : 'Create Location'}
          </Button>
          <Button type="button" variant="ghost" onClick={goBack}>
            Cancel
          </Button>
        </div>
      </form>

      {/* 416 (#1431, mock-up screen 1): the zone, drawn and saved on its own (outside the form above). Shown once the
          location exists and has loaded, keyed on it so a reload starts from the stored shape. */}
      {isEdit && location && (
        <Card className="mt-6 max-w-2xl" data-testid="location-zone-card">
          <h2 className="mb-1 text-sm font-semibold text-ink">Zone</h2>
          <p className="mb-3 text-xs text-muted">
            The area a rented car picked up here should stay in. Driving outside it is measured and
            may be charged.
          </p>
          <ZoneEditor
            key={String(id)}
            locationId={String(id)}
            zone={location.zone ?? null}
            centre={
              location.latitude != null && location.longitude != null
                ? { latitude: location.latitude, longitude: location.longitude }
                : null
            }
            canEdit={canEdit}
          />
        </Card>
      )}
    </div>
  );
}
